import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { bulkDistributeRequests } from "../../api/requestsBulkApi";
import BulkActionConfirmStep from "./BulkActionConfirmStep";
import BulkFormImpactHint from "./BulkFormImpactHint";

export default function DistributeRequestsDialog({
  open,
  selectedIds = [],
  operators = [],
  onClose,
  onDistributed,
}) {
  const ids = useMemo(
    () => [...new Set(selectedIds.map((id) => Number(id)).filter((n) => Number.isFinite(n) && n > 0))],
    [selectedIds]
  );

  const [selectedOperators, setSelectedOperators] = useState([]);
  const [step, setStep] = useState("form");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const operatorOptions = useMemo(
    () =>
      (operators || [])
        .filter((u) => u?.id != null)
        .map((u) => ({
          id: Number(u.id),
          label: u.nome || u.name || `Usuário ${u.id}`,
        })),
    [operators]
  );

  useEffect(() => {
    if (!open) return;
    setSelectedOperators([]);
    setStep("form");
    setError(null);
  }, [open]);

  const handleClose = () => {
    if (submitting) return;
    setSelectedOperators([]);
    setStep("form");
    setError(null);
    onClose?.();
  };

  const changeLines = useMemo(
    () => [
      {
        label: "Modo",
        value: "Rodízio entre os operadores selecionados",
      },
      {
        label: "Operadores",
        value: selectedOperators.map((o) => o.label).join(", ") || "—",
      },
      {
        label: "Status após distribuir",
        value: "Pendente (fila)",
      },
    ],
    [selectedOperators]
  );

  const handleGoToConfirm = () => {
    if (!ids.length) {
      setError("Selecione pelo menos 1 solicitação.");
      return;
    }
    if (!selectedOperators.length) {
      setError("Selecione pelo menos 1 operador.");
      return;
    }
    setError(null);
    setStep("confirm");
  };

  const handleSubmit = async () => {
    const userIds = selectedOperators.map((o) => o.id);
    setSubmitting(true);
    setError(null);
    try {
      await bulkDistributeRequests(ids, userIds);
      handleClose();
      onDistributed?.();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Erro ao distribuir solicitações.");
    } finally {
      setSubmitting(false);
    }
  };

  if (step === "confirm") {
    return (
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Revisar distribuição</DialogTitle>
        {error && (
          <Box px={3} pt={1}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}
        <BulkActionConfirmStep
          ids={ids}
          actionTitle="Revisar distribuição"
          impactVerb="distribuídas"
          irreversible
          actionDescription="Cada solicitação será atribuída a um operador, em sequência, na ordem da lista."
          changeLines={changeLines}
          warnings={[
            "A responsabilidade atual de cada solicitação será substituída pelo operador designado.",
          ]}
          onBack={() => {
            setError(null);
            setStep("form");
          }}
          onConfirm={handleSubmit}
          submitting={submitting}
          confirmButtonLabel="Confirmar distribuição"
        />
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Distribuir solicitações</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <BulkFormImpactHint count={ids.length} impactVerb="distribuídas" irreversible />

          {error && <Alert severity="error">{error}</Alert>}

          <Autocomplete
            multiple
            options={operatorOptions}
            value={selectedOperators}
            onChange={(_, value) => setSelectedOperators(value)}
            getOptionLabel={(opt) => opt.label}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            disabled={submitting}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip {...getTagProps({ index })} key={option.id} label={option.label} size="small" />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="Operadores" placeholder="Selecione os operadores" size="small" />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={handleGoToConfirm} disabled={submitting || !ids.length}>
          Revisar distribuição
        </Button>
      </DialogActions>
    </Dialog>
  );
}
